/* eslint-disable import/no-cycle */
import { get, map } from 'lodash'
import { ThunkResult } from '../index'
import { FORM_DOCS, FORM_DOC } from './formDocsTypes'
import { getReq } from '../../utils/request'
import { IResetStore } from '../generalTypes'
import { IResponsePagination, ISelectOptionItem } from '../../types/interfaces'
import { PAGINATION } from '../../utils/enums'
import { formatDateWithTime, normalizeQueryParams } from '../../utils/helper'

export type FormDocsActions = IResetStore | IGetFormDocsAction | IGetFormDocAction

interface IGetFormDocsAction {
	type: FORM_DOCS
	payload: IFormDocsPayload
}

interface IGetFormDocAction {
	type: FORM_DOC
	payload: IFormDocPayload
}

interface IFormDocItem {
	id: number
	name: string
	status: string
	state: boolean
	database: string
	type: string
	createdAt: string
	author: string
	editor: string
	updatedAt: string
	deletedAt: string
	queue: string
}

interface IFormDocDetailItem {
	id: number
	name: string
	businessID: string
	taxID: string
	vatID: string
	isVATPayer: boolean
	web: string
	phone: string
	email: string
	fax: string
}

interface IFormDocBranch {
	id: number
	code: number
	name: string
	formDoc: {
		id: number
		name: string
	}
}

export interface IFormDocDateChange {
	date: number
	author: string
	id: string
}

export interface IQueue {
	id: number
	title: number
}

export interface IAuthor {
	id: number
	fullName: string
	officialName: string
}

export interface IFormDocTableItem {
	key: number
	id: number
	author: IAuthor
	fullName: string
	database: string
	editor: IAuthor
	finalStatus: string
	formItems: any[]
	inputStoredProcedure: string
	name: string
	outputStoredProcedureAfterSave: string
	outputStoredProcedureBeforeSave: string
	queue: IQueue
	title: string
	state: boolean
	status: string
	type: string
	iframeUrl: string
	createdAt: string
	updatedAt: string
	created: IFormDocDateChange
	lastEdit: IFormDocDateChange
}

export interface IFormDocBranchesTableItem {
	key: number
	id: number
	code: string
	name: string
	address: string
}

export interface IFormDocBranchesByFormDocIDPayload {
	tableData: IFormDocBranchesTableItem[] | null
	pagination: IResponsePagination | null
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface IFormDocBranchPayload {
	// data: IBranchItem | null
}

export interface IFormDocsPayload {
	originalData: IFormDocItem[] | null
	formDocsOptions: ISelectOptionItem[] | null
	tableData: IFormDocTableItem[] | []
	pagination: IResponsePagination | null
}

export interface IFormDocDuplicatePayload {
	formName: string | null
}

export interface IFormDocPayload {
	data: IFormDocDetailItem | null
}

export interface IFormDocBranchesPayload {
	originalData: IFormDocBranch[] | null
	branchesOptions: ISelectOptionItem[] | null
	pagination: IResponsePagination | null
}

export const getFormDocs =
	(page = 1, limit?: number | null, queryParams = {}): ThunkResult<Promise<any>> =>
	async (dispatch) => {
		try {
			dispatch({
				type: FORM_DOCS.FORM_DOCS_LOAD_START
			})
			const pageLimit = limit || PAGINATION.limit
			const { data } = await getReq('/api/admin/forms/', { page, limit: pageLimit, ...normalizeQueryParams(queryParams) })
			const originalData: IFormDocTableItem[] = get(data, 'data.forms')
			const tableData = map(originalData, (column) => ({
				key: column.id,
				id: column.id,
				fullName: column.fullName,
				name: column.name,
				status: column.status,
				state: column.state,
				database: column.database,
				type: column.type,
				createdAt: column.createdAt,
				updatedAt: column.updatedAt,
				queue: column.queue,
				iframeUrl: column.iframeUrl,
				created: {
					author: column?.author?.officialName,
					date: formatDateWithTime(column.createdAt)
				},
				lastEdit: {
					author: column?.editor?.officialName || '',
					date: formatDateWithTime(column.updatedAt),
					id: column?.editor?.id
				}
			}))

			const formDocsOptions = map(originalData, (item) => ({
				key: item.id,
				label: item.name
			}))

			const payload = {
				originalData,
				tableData,
				formDocsOptions,
				pagination: get(data, 'data.pagination')
			}
			dispatch({
				type: FORM_DOCS.FORM_DOCS_LOAD_DONE,
				payload
			})

			return payload
		} catch (e) {
			dispatch({
				type: FORM_DOCS.FORM_DOCS_LOAD_FAIL
			})
			return e
		}
	}

export const getFormDoc =
	(formDocID: number): ThunkResult<Promise<any>> =>
	async (dispatch) => {
		try {
			dispatch({ type: FORM_DOC.FORM_DOC_LOAD_START })
			const { data } = await getReq(`/api/v1/formDocs/${formDocID}`, undefined)

			const payload = {
				data: get(data, 'forms')
			}

			dispatch({ type: FORM_DOC.FORM_DOC_LOAD_DONE, payload })
			return payload
		} catch (e) {
			dispatch({ type: FORM_DOC.FORM_DOC_LOAD_FAIL })
			return e
		}
	}
